/**
 * Auto-reload sur chunk-loading-error post-deploy.
 *
 * Quand un user a une vieille version de l'app ouverte au moment d'un deploy,
 * le `index.html` cached référence des chunks JS/CSS dont le hash a changé.
 * Lors de la première navigation lazy (React.lazy / dynamic import), le
 * navigateur tente de fetch un fichier qui n'existe plus → Firebase Hosting
 * renvoie `index.html` (HTML) → MIME type mismatch + erreur.
 *
 * Stratégie : intercepter l'erreur, set un flag sessionStorage, recharger.
 * Le flag est nettoyé 5 s après `window.load` pour permettre un futur reload
 * sur un deploy suivant, sans risquer une boucle infinie si l'erreur revient.
 */

const RELOAD_KEY = 'fitd-chunk-reload-attempted';
const CLEAR_DELAY_MS = 5_000;

// Patterns Chrome/Firefox + Safari iOS (qui balance "'text/html' is not a valid JavaScript MIME type.")
// ⚠️ Chrome dit "Failed to fetch dynamically imported module", Firefox dit
// "error loading dynamically imported module" — deux formulations distinctes,
// il faut les deux (sinon retry + auto-reload muets sur Firefox).
const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Failed to load module script|module script.+MIME type|is not a valid JavaScript MIME type|Loading chunk \d+ failed|Loading CSS chunk/i;

export function isChunkError(message: string | null | undefined): boolean {
  if (!message) return false;
  return CHUNK_ERROR_PATTERN.test(message);
}

function tryReload(reason: string): void {
  if (typeof sessionStorage === 'undefined' || typeof window === 'undefined') return;
  if (sessionStorage.getItem(RELOAD_KEY)) {
    console.warn('[chunkErrorReload] reload already attempted, giving up:', reason);
    return;
  }
  console.warn('[chunkErrorReload] stale chunk detected, reloading:', reason);
  sessionStorage.setItem(RELOAD_KEY, '1');
  // Cache-buster (`?_r=`) plutôt qu'un `reload()` nu : un simple reload peut
  // resservir un `index.html` périmé depuis le cache navigateur/edge (le HTML
  // est `no-cache`, mais un cache edge « Cache Everything » peut le figer), ce
  // qui recharge les MÊMES chunks morts → blanc persistant. Une URL unique
  // force un `index.html` frais (bypass de tout cache) → nouveaux chunks. Le
  // param est retiré de l'URL par le garde inline de `index.html` après `load`.
  try {
    const u = new URL(window.location.href);
    u.searchParams.set('_r', Date.now().toString(36));
    window.location.replace(u.toString());
  } catch {
    window.location.reload();
  }
}

/**
 * Installs window-level handlers. Idempotent : calling twice is safe but
 * registers two listeners (each guarded by RELOAD_KEY so harmless).
 */
export function installChunkErrorReload(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event) => {
    const msg = (event as ErrorEvent).error?.message || (event as ErrorEvent).message || '';
    if (isChunkError(msg)) tryReload(`error: ${msg}`);
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = (event as PromiseRejectionEvent).reason;
    const msg = reason?.message || (typeof reason === 'string' ? reason : String(reason ?? ''));
    if (isChunkError(msg)) tryReload(`rejection: ${msg}`);
  });

  // Clear the flag once the page has stabilized so the NEXT deploy can also
  // trigger a reload. Delay before clear avoids resetting the guard if a
  // chunk fails immediately after reload (boucle infinie).
  window.addEventListener('load', () => {
    setTimeout(() => {
      try {
        sessionStorage.removeItem(RELOAD_KEY);
      } catch {
        // sessionStorage may be unavailable (private mode + Safari)
      }
    }, CLEAR_DELAY_MS);
  });
}
